import { useState, useEffect, useCallback } from 'react'

/**
 * Hook for localStorage-backed state with React sync.
 * SSR-safe with typeof window check.
 */
export function useLocalStorage<T>(
  key: string,
  defaultValue: T
): [T, (value: T) => void] {
  // Initialize from localStorage or default
  const [value, setValue] = useState<T>(() => {
    if (typeof window === 'undefined') return defaultValue
    try {
      const stored = localStorage.getItem(key)
      return stored !== null ? JSON.parse(stored) : defaultValue
    } catch {
      return defaultValue
    }
  })

  // Sync to localStorage when value changes
  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(value))
    } catch {
      // Ignore write errors (e.g., quota exceeded)
    }
  }, [key, value])

  const setValueAndPersist = useCallback((newValue: T) => {
    setValue(newValue)
  }, [])

  return [value, setValueAndPersist]
}
